import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Info, Save, Plus, Trash2, KeyRound, Webhook, LinkIcon } from 'lucide-react';
import {
  PageSettingsApi,
  FbOAuthApi,
  type PageSettingsItem,
  type PageSettingsWrite,
  type Brand,
} from '@/lib/admin-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useRole } from '@/lib/use-role';
import { useBrands } from '@/lib/useBrands';
import { toast } from 'sonner';

function HelpBanner() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-3 text-sm">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 font-medium text-blue-800 hover:underline w-full text-left"
      >
        <Info size={16} />
        Page Settings — toggle reply เปิด/ปิดในแต่ละเพจ — {open ? 'ซ่อน' : 'ดู'}
      </button>
      {open && (
        <div className="mt-3 text-xs space-y-2 text-muted-foreground">
          <p>
            <strong>page_id</strong> — Facebook Page ID (ดูจาก Meta Business Suite → Settings → Page Info).
            ต้องตรงกับ <code>FB_PAGE_ID_*</code> ใน wrangler.toml.
          </p>
          <p>
            <strong>reply_enabled</strong> — สวิตช์หลัก. <code>on</code> = บอตตอบอัตโนมัติ, <code>off</code> = log only (แอดมินตอบเอง).
            <br />
            <strong>แนะนำ rollout:</strong> เปิดเฉพาะ sandbox ก่อน → test → เปิดทีละเพจจริง.
          </p>
          <p>
            <strong>greeting_text</strong> — ข้อความที่บอตใช้เมื่อลูกค้าทักทาย แต่ KB ไม่มี match พอดี (RAG miss).
          </p>
          <p>
            <strong>fallback_text</strong> — ข้อความเมื่อไม่รู้จะตอบยังไง (catch-all).
          </p>
          <p>
            แก้แล้ว cache จะถูกล้างอัตโนมัติ — บอตอัพเดททันที.
          </p>
        </div>
      )}
    </div>
  );
}

export default function PagesSettingsPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const { canWrite, canAdmin } = useRole();

  const q = useQuery({
    queryKey: ['page-settings'],
    queryFn: () => PageSettingsApi.list(),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: PageSettingsWrite }) =>
      PageSettingsApi.update(id, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-settings'] });
      toast.success('บันทึกแล้ว');
    },
    onError: (e) => toast.error(`ผิดพลาด: ${e.message}`),
  });

  const createMut = useMutation({
    mutationFn: (data: PageSettingsWrite) => PageSettingsApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-settings'] });
      setAdding(false);
      toast.success('เพิ่มเพจแล้ว');
    },
    onError: (e) => toast.error(`ผิดพลาด: ${e.message}`),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => PageSettingsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-settings'] });
      toast.success('ลบแล้ว');
    },
  });

  const subscribeMut = useMutation({
    mutationFn: (id: number) => PageSettingsApi.subscribe(id),
    onSuccess: () => toast.success('เชื่อม webhook แล้ว — FB จะส่ง message events มาที่ worker'),
    onError: (e) => toast.error(`subscribe ล้มเหลว: ${e.message}`),
  });

  const oauthMut = useMutation({
    mutationFn: () => FbOAuthApi.start(),
    onSuccess: (r) => {
      // เปิด FB consent dialog ใน tab เดิม (กดยืนยัน → กลับมาที่หน้านี้พร้อม ?fb_oauth=ok)
      window.location.href = r.authorizeUrl;
    },
    onError: (e) => toast.error(`เริ่ม OAuth ล้มเหลว: ${e.message}`),
  });

  // อ่าน query params จาก redirect callback → toast สำเร็จ/ผิดพลาด
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('fb_oauth');
    if (!status) return;
    if (status === 'ok') {
      const updated = params.get('updated') || '';
      toast.success(`เชื่อมต่อ Facebook สำเร็จ — อัพเดตเพจ: ${updated || '(ไม่มี)'}`);
      qc.invalidateQueries({ queryKey: ['page-settings'] });
    } else {
      const reason = params.get('reason') || 'unknown';
      const fbmsg = params.get('fbmsg') || '';
      toast.error(`เชื่อมต่อ Facebook ล้มเหลว: ${reason}${fbmsg ? ` — ${fbmsg.slice(0, 200)}` : ''}`);
    }
    // ล้าง query params ออกจาก URL หลังอ่านแล้ว
    window.history.replaceState({}, '', window.location.pathname);
  }, [qc]);

  return (
    <div className="space-y-4">
      <HelpBanner />

      {canAdmin && (
        <div className="border border-emerald-200 bg-emerald-50/50 rounded-lg p-3 space-y-2 text-sm">
          <div className="font-semibold flex items-center gap-2">
            <LinkIcon size={16} /> วิธีรับ Page Access Token (สำหรับ FB Sync รูปสินค้า)
          </div>
          <ol className="text-xs text-muted-foreground space-y-1 pl-5 list-decimal">
            <li>
              คลิก{' '}
              <a
                href="https://developers.facebook.com/tools/explorer/?method=GET&path=me%2Faccounts%3Ffields%3Dname%2Caccess_token%2Cid&version=v21.0"
                target="_blank"
                rel="noreferrer"
                className="text-emerald-700 underline font-medium"
              >
                เปิด Graph API Explorer
              </a>
              {' '}→ มุมบนขวา เลือก app <strong>cher-bot</strong>
            </li>
            <li>
              กดปุ่ม <strong>Generate Access Token</strong> → ติ๊กเลือก permissions:
              <code className="bg-white px-1 mx-0.5 rounded text-[10px]">pages_messaging</code>
              <code className="bg-white px-1 mx-0.5 rounded text-[10px]">pages_show_list</code>
              <code className="bg-white px-1 mx-0.5 rounded text-[10px]">pages_read_engagement</code>
              <code className="bg-white px-1 mx-0.5 rounded text-[10px]">pages_manage_metadata</code>
              → กด Generate → Authorize
            </li>
            <li>
              กดปุ่ม Submit → response จะแสดง list เพจพร้อม <code>access_token</code> ของแต่ละเพจ
            </li>
            <li>
              ก๊อปปี้ access_token ของเพจ → กลับมาที่หน้านี้ → กด <strong>แก้</strong> ที่เพจตรงกัน
              → วางใน field <strong>access_token</strong> → กด Save
            </li>
          </ol>
          <div className="text-[10px] text-muted-foreground">
            💡 Page Access Token ที่ได้จาก long-lived user token เป็นแบบ never-expire — ทำครั้งเดียวพอ
          </div>
          {oauthMut.isPending !== undefined && false && (
            <Button onClick={() => oauthMut.mutate()} disabled>
              {/* OAuth flow disabled — invalid scopes ใน FB Login for Business */}
            </Button>
          )}
        </div>
      )}

      {canWrite && (
        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus size={14} className="mr-1" /> เพิ่มเพจ
          </Button>
        </div>
      )}

      {q.data?.map((p) => (
        <PageCard
          key={p.id}
          item={p}
          canWrite={canWrite}
          canAdmin={canAdmin}
          onSave={(patch) => updateMut.mutate({ id: p.id, patch })}
          onDelete={() => confirm(`ลบเพจ "${p.pageName}"?`) && deleteMut.mutate(p.id)}
          onSubscribe={() => subscribeMut.mutate(p.id)}
        />
      ))}

      {adding && (
        <PageEditor
          item={null}
          onCancel={() => setAdding(false)}
          onSave={(data) => createMut.mutate(data)}
        />
      )}
    </div>
  );
}

function PageCard({
  item,
  canWrite,
  canAdmin,
  onSave,
  onDelete,
  onSubscribe,
}: {
  item: PageSettingsItem;
  canWrite: boolean;
  canAdmin: boolean;
  onSave: (patch: PageSettingsWrite) => void;
  onDelete: () => void;
  onSubscribe: () => void;
}) {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <PageEditor
        item={item}
        onCancel={() => setEditing(false)}
        onSave={(patch) => {
          onSave(patch);
          setEditing(false);
        }}
      />
    );
  }

  return (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold">{item.pageName}</span>
            <code className="text-xs text-muted-foreground">{item.pageId}</code>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                item.replyEnabled ? 'status-active' : 'status-inactive'
              }`}
            >
              {item.replyEnabled ? 'reply: on' : 'reply: off'}
            </span>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border status-info">
              brand: {item.brand}
            </span>
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                item.hasAccessToken ? 'status-active' : 'status-inactive'
              }`}
              title="Facebook Page Access Token"
            >
              <KeyRound size={10} className="mr-0.5" />
              {item.hasAccessToken ? 'token: saved' : 'no token'}
            </span>
          </div>
          <div className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-2 text-xs">
            <div>
              <span className="text-muted-foreground">greeting:</span>{' '}
              <span className="line-clamp-2">{item.greetingText ?? '—'}</span>
            </div>
            <div>
              <span className="text-muted-foreground">fallback:</span>{' '}
              <span className="line-clamp-2">{item.fallbackText ?? '—'}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {canWrite && (
            <Button
              size="sm"
              variant={item.replyEnabled ? 'outline' : 'default'}
              className={item.replyEnabled ? '' : 'btn-cta-primary'}
              onClick={() => onSave({ replyEnabled: !item.replyEnabled })}
            >
              {item.replyEnabled ? 'ปิด reply' : 'เปิด reply'}
            </Button>
          )}
          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              แก้
            </Button>
          )}
          {canAdmin && item.hasAccessToken && (
            <Button
              size="sm"
              variant="outline"
              onClick={onSubscribe}
              title="subscribe FB webhook ให้เพจนี้"
            >
              <Webhook size={12} className="mr-1" /> subscribe
            </Button>
          )}
          {canAdmin && (
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50"
              onClick={onDelete}
            >
              <Trash2 size={14} />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function PageEditor({
  item,
  onSave,
  onCancel,
}: {
  item: PageSettingsItem | null;
  onSave: (data: PageSettingsWrite) => void;
  onCancel: () => void;
}) {
  const { brands } = useBrands();
  const [draft, setDraft] = useState<PageSettingsWrite>(
    item
      ? { ...item }
      : {
          pageId: '',
          pageName: '',
          brand: 'scrub',
          replyEnabled: false,
          greetingText: '',
          fallbackText: '',
          notes: '',
        },
  );
  const [accessToken, setAccessToken] = useState('');
  const [verifyToken, setVerifyToken] = useState('');

  return (
    <div className="border border-amber-300 rounded-lg p-4 space-y-3 bg-amber-50/30">
      <h3 className="font-semibold text-sm">
        {item ? `แก้: ${item.pageName}` : 'เพิ่มเพจใหม่'}
      </h3>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">page_id (FB Page ID)</label>
          <Input
            value={draft.pageId ?? ''}
            disabled={!!item}
            placeholder="858404420680831"
            onChange={(e) => setDraft({ ...draft, pageId: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">page_name</label>
          <Input
            value={draft.pageName ?? ''}
            onChange={(e) => setDraft({ ...draft, pageName: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">brand</label>
          <select
            className="w-full border border-input rounded-md h-9 px-3 text-sm"
            value={draft.brand ?? 'scrub'}
            onChange={(e) => setDraft({ ...draft, brand: e.target.value as Brand })}
          >
            {brands.map((b) => (
              <option key={b.brand_key} value={b.brand_key}>{b.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.replyEnabled ?? false}
              onChange={(e) =>
                setDraft({ ...draft, replyEnabled: e.target.checked })
              }
            />
            reply_enabled (บอตตอบอัตโนมัติ)
          </label>
        </div>
        <div>
          <label className="text-xs text-muted-foreground flex items-center gap-2">
            <input
              type="checkbox"
              checked={draft.isTestPage ?? false}
              onChange={(e) => setDraft({ ...draft, isTestPage: e.target.checked })}
            />
            เพจทดสอบ (ใช้บอทขายตัวใหม่ · brand ด้านบน = แบรนด์ที่กำลังทดสอบ)
          </label>
          {draft.isTestPage && (
            <Input
              className="mt-1"
              placeholder="* = ตอบทุกคน · หรือใส่ PSID ของบัญชีทดสอบ คั่นด้วย ,"
              value={draft.testPsids ?? ''}
              onChange={(e) => setDraft({ ...draft, testPsids: e.target.value })}
            />
          )}
          <label className="text-xs text-muted-foreground block mt-2">แอดมินรับแจ้งผลตรวจสลิปทาง Messenger (PSID คั่นด้วย , · แอดมินต้องเคยทักเพจนี้)</label>
          <Input
            className="mt-1"
            placeholder="PSID ของแอดมิน"
            value={draft.adminPsids ?? ''}
            onChange={(e) => setDraft({ ...draft, adminPsids: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">greeting_text</label>
          <textarea
            className="w-full text-sm border border-input rounded-md p-2 min-h-[60px]"
            value={draft.greetingText ?? ''}
            onChange={(e) => setDraft({ ...draft, greetingText: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">fallback_text</label>
          <textarea
            className="w-full text-sm border border-input rounded-md p-2 min-h-[60px]"
            value={draft.fallbackText ?? ''}
            onChange={(e) => setDraft({ ...draft, fallbackText: e.target.value })}
          />
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">notes (internal)</label>
          <Input
            value={draft.notes ?? ''}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
        </div>

        <div className="col-span-2 border-t border-amber-200 pt-3 mt-2">
          <div className="text-xs font-semibold text-amber-900 mb-1">
            🔐 Facebook Page Tokens (encrypted)
          </div>
          <p className="text-xs text-muted-foreground mb-2">
            เก็บใน DB แบบ encrypt — กรอกใหม่เฉพาะเวลาต้อง rotate เท่านั้น
            (เว้นว่าง = ใช้ค่าเดิม).
            {item?.hasAccessToken ? ' ✓ มี token แล้ว' : ' ⚠ ยังไม่มี — ต้องกรอกก่อนจึงจะ subscribe webhook ได้'}
          </p>
        </div>
        <div className="col-span-2">
          <label className="text-xs text-muted-foreground">access_token (Page Access Token)</label>
          <Input
            type="password"
            placeholder={item?.hasAccessToken ? '••• เก็บไว้แล้ว (กรอกใหม่ = rotate)' : 'EAAGm...'}
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">verify_token (webhook)</label>
          <Input
            type="password"
            placeholder={item?.hasVerifyToken ? '••• เก็บไว้แล้ว' : 'cher_verify_xxx'}
            value={verifyToken}
            onChange={(e) => setVerifyToken(e.target.value)}
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">fb_app_id (optional)</label>
          <Input
            value={draft.fbAppId ?? ''}
            onChange={(e) => setDraft({ ...draft, fbAppId: e.target.value })}
          />
        </div>
      </div>
      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={onCancel}>
          ยกเลิก
        </Button>
        <Button
          size="sm"
          className="btn-cta-primary"
          onClick={() => {
            if (!draft.pageId || !draft.pageName) {
              toast.error('กรอก page_id + page_name');
              return;
            }
            const payload: PageSettingsWrite = { ...draft };
            if (accessToken.trim()) payload.accessToken = accessToken.trim();
            if (verifyToken.trim()) payload.verifyToken = verifyToken.trim();
            onSave(payload);
          }}
        >
          <Save size={14} className="mr-1" /> บันทึก
        </Button>
      </div>
    </div>
  );
}
